import * as RdxPopover from '@radix-ui/react-popover';
import {
  CheckIcon,
  ChevronDownIcon,
  GearIcon,
  Pencil1Icon,
  PlusIcon,
} from '@radix-ui/react-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { IBadge } from '@clickersphere/core/entities/IBadge';
import { BadgeColorEnum } from '@clickersphere/core/enums/BadgeColorEnum';
import { useBadges } from '../../../app/hooks/badge/useBadges';
import { useCreateBadge } from '../../../app/hooks/badge/useCreateBadge';
import { useUpdateBadge } from '../../../app/hooks/badge/useUpdateBadge';
import useToast from '../../../app/hooks/useToast';
import { useStore } from '../../../app/store';
import cn from '../../../app/utils/cn';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Input } from '../Input';
import { TextButton } from '../TextButton';
import BadgeChip from './BadgeChip';
import ColorPicker from './ColorPicker';

interface IBadgeComboboxProps {
  selectedBadgeIds: string[];
  onChange: (badgeIds: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  hideCreate?: boolean;
  maxVisibleBadges?: number;
}

export default function BadgeCombobox({
  selectedBadgeIds,
  onChange,
  disabled,
  placeholder,
  className,
  hideCreate = false,
  maxVisibleBadges = 2,
}: IBadgeComboboxProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { badges, isBadgesLoading } = useBadges();
  const { createBadgeAsync, createBadgeIsPending } = useCreateBadge();
  const { updateBadgeAsync, updateBadgeIsPending } = useUpdateBadge();

  const handleOpenManageBadgesModal = useStore(
    useShallow((state) => state.modal.handleOpenManageBadgesModal),
  );

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [editingBadgeId, setEditingBadgeId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState<BadgeColorEnum>(
    BadgeColorEnum.BLUE,
  );

  const isEditing = editingBadgeId !== null;
  const isFormBusy = createBadgeIsPending || updateBadgeIsPending;

  const selectedBadges = useMemo(
    () => badges.filter((b) => selectedBadgeIds.includes(b.id)),
    [badges, selectedBadgeIds],
  );

  const visibleSelectedBadges = useMemo(
    () => selectedBadges.slice(0, maxVisibleBadges),
    [selectedBadges, maxVisibleBadges],
  );

  const overflowSelectedCount =
    selectedBadges.length - visibleSelectedBadges.length;

  const filteredBadges = useMemo(() => {
    if (!search.trim()) return badges;
    const q = search.trim().toLowerCase();
    return badges.filter((b) => b.name.toLowerCase().includes(q));
  }, [badges, search]);

  const exactMatch = useMemo(
    () =>
      badges.some((b) => b.name.toLowerCase() === search.trim().toLowerCase()),
    [badges, search],
  );

  function toggleBadge(badge: IBadge) {
    if (selectedBadgeIds.includes(badge.id)) {
      onChange(selectedBadgeIds.filter((id) => id !== badge.id));
    } else {
      onChange([...selectedBadgeIds, badge.id]);
    }
  }

  function handleRemove(badge: IBadge) {
    onChange(selectedBadgeIds.filter((id) => id !== badge.id));
  }

  function openCreateForm() {
    setEditingBadgeId(null);
    setFormName(search.trim());
    setFormColor(BadgeColorEnum.BLUE);
    setShowForm(true);
  }

  function openEditForm(badge: IBadge) {
    setEditingBadgeId(badge.id);
    setFormName(badge.name);
    setFormColor(badge.color);
    setShowForm(true);
  }

  function resetForm() {
    setShowForm(false);
    setEditingBadgeId(null);
    setFormName('');
  }

  async function submitForm() {
    const trimmed = formName.trim();
    if (!trimmed) return;
    try {
      if (editingBadgeId) {
        await updateBadgeAsync({
          id: editingBadgeId,
          data: { name: trimmed, color: formColor },
        });
        toast(t('badge.toast.updated'), 'success');
      } else {
        const created = await createBadgeAsync({
          name: trimmed,
          color: formColor,
        });
        toast(t('badge.toast.created'), 'success');
        onChange([...selectedBadgeIds, created.id]);
        setSearch('');
      }
      resetForm();
    } catch {
      toast(
        editingBadgeId
          ? t('badge.toast.error.update')
          : t('badge.toast.error.create'),
        'error',
      );
    }
  }

  function handleManageBadges() {
    setOpen(false);
    handleOpenManageBadgesModal();
  }

  return (
    <div className={cn('w-full min-w-[200px]', className)}>
      <RdxPopover.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            resetForm();
            setSearch('');
          }
        }}
      >
        <RdxPopover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={t('badge.combobox.triggerAriaLabel')}
            className={cn(
              'bg-surface-input border-border-input text-text-primary flex h-10 w-full items-center justify-between gap-2 rounded border-2 border-solid px-2 py-1.5 text-sm shadow-md outline-none',
              'focus:border-green-600 dark:focus:border-green-400',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <span className="flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-hidden">
              {selectedBadges.length === 0 ? (
                <span className="text-text-tertiary truncate">
                  {placeholder ?? t('badge.combobox.placeholder')}
                </span>
              ) : (
                <>
                  {visibleSelectedBadges.map((badge) => (
                    <BadgeChip
                      key={badge.id}
                      badge={badge}
                      size="xs"
                      onRemove={handleRemove}
                      className="shrink-0"
                    />
                  ))}
                  {overflowSelectedCount > 0 && (
                    <span className="text-text-secondary shrink-0 text-xs font-medium">
                      +{overflowSelectedCount}
                    </span>
                  )}
                </>
              )}
            </span>
            <ChevronDownIcon className="text-text-tertiary shrink-0" />
          </button>
        </RdxPopover.Trigger>

        <RdxPopover.Portal>
          <RdxPopover.Content
            className="bg-surface-card border-border-default z-50 mt-1 w-[var(--radix-popover-trigger-width)] min-w-[260px] rounded-lg border p-2 shadow-lg"
            sideOffset={4}
            align="start"
          >
            {!showForm && (
              <>
                <div className="mb-2">
                  <Input
                    name="badge-search"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('badge.combobox.searchPlaceholder')}
                    aria-label={t('badge.combobox.searchPlaceholder')}
                  />
                </div>

                <ul
                  role="listbox"
                  aria-multiselectable="true"
                  className="flex max-h-60 flex-col gap-0.5 overflow-y-auto"
                >
                  {isBadgesLoading && (
                    <li className="text-text-tertiary p-2 text-xs">
                      {t('badge.combobox.loading')}
                    </li>
                  )}

                  {!isBadgesLoading && filteredBadges.length === 0 && (
                    <li className="text-text-tertiary p-2 text-xs">
                      {t('badge.combobox.empty')}
                    </li>
                  )}

                  {filteredBadges.map((badge) => {
                    const checked = selectedBadgeIds.includes(badge.id);
                    return (
                      <li
                        key={badge.id}
                        role="option"
                        aria-selected={checked}
                        className="hover:bg-hover-subtle dark:hover:bg-hover-medium flex items-center gap-1 rounded"
                      >
                        <button
                          type="button"
                          onClick={() => toggleBadge(badge)}
                          className="flex flex-1 items-center justify-between gap-2 px-2 py-1.5 text-left"
                        >
                          <BadgeChip
                            badge={badge}
                            size="sm"
                            removable={false}
                          />
                          {checked && (
                            <CheckIcon className="text-text-primary shrink-0" />
                          )}
                        </button>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditForm(badge);
                          }}
                          aria-label={t('badge.combobox.editAriaLabel', {
                            name: badge.name,
                          })}
                          className="shrink-0"
                        >
                          <Pencil1Icon className="h-3.5 w-3.5" />
                        </IconButton>
                      </li>
                    );
                  })}
                </ul>

                <div className="border-border-default mt-2 flex flex-col border-t pt-2">
                  {!hideCreate && (
                    <TextButton
                      onClick={openCreateForm}
                      disabled={!search.trim() && exactMatch}
                      className="text-text-primary hover:bg-hover-subtle dark:hover:bg-hover-medium w-full justify-start gap-2 px-2 py-1.5"
                    >
                      <PlusIcon className="shrink-0" />
                      <span className="truncate">
                        {search.trim()
                          ? t('badge.combobox.createNamed', {
                              name: search.trim(),
                            })
                          : t('badge.combobox.createNew')}
                      </span>
                    </TextButton>
                  )}
                  <TextButton
                    onClick={handleManageBadges}
                    className="hover:bg-hover-subtle dark:hover:bg-hover-medium w-full justify-start gap-2 px-2 py-1.5"
                  >
                    <GearIcon className="shrink-0" />
                    <span className="truncate">
                      {t('badge.combobox.manage')}
                    </span>
                  </TextButton>
                </div>
              </>
            )}

            {showForm && (
              <div className="flex flex-col gap-3">
                <h3 className="text-text-primary text-sm font-semibold">
                  {isEditing
                    ? t('badge.combobox.editTitle')
                    : t('badge.combobox.createTitle')}
                </h3>
                <Input
                  name="badge-name"
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={t('badge.combobox.namePlaceholder')}
                  aria-label={t('badge.combobox.namePlaceholder')}
                  maxLength={50}
                />
                <ColorPicker value={formColor} onChange={setFormColor} />
                <div className="flex gap-2">
                  <Button
                    color="ghost"
                    labelPath="badge.combobox.cancel"
                    onClick={resetForm}
                    disabled={isFormBusy}
                  />
                  <Button
                    color="primary"
                    labelPath={
                      isEditing
                        ? 'badge.combobox.save'
                        : 'badge.combobox.confirm'
                    }
                    onClick={submitForm}
                    disabled={!formName.trim() || isFormBusy}
                    isLoading={isFormBusy}
                  />
                </div>
              </div>
            )}
          </RdxPopover.Content>
        </RdxPopover.Portal>
      </RdxPopover.Root>
    </div>
  );
}
